import { useMutation, useQueryClient } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/elements/select'
import { Textarea } from '@/components/elements/textarea'
import { FileUploader } from '@/components/FileUploader'
import { Modal } from '@/components/Modal'
import { addCourseAsset } from '@/services/course_assets.service'
import { toast } from '@/utils/helpers'

// Per-type S3 acceptedFileTypes mirror what ContentBlock allows.
// Articulate is now available to facilities too (no longer RescueX-only).
// PowerPoint is disabled everywhere for now (no working viewer); decks are
// converted to Articulate as needed. Re-add once its viewer works (see
// CourseForm/ContentBlock content type lists).
const ASSET_TYPES = [
  { id: 'Video', label: 'Video', fileTypes: ['video/mp4', 'video/webm'] },
  { id: 'PDF', label: 'PDF', fileTypes: ['application/pdf'] },
  { id: 'Articulate', label: 'Articulate', fileTypes: ['application/zip'] },
]

const CreateAssetModal = ({ entityId, organizationId, close, onCreated }) => {
  const form = useForm({
    defaultValues: {
      name: '',
      description: '',
      type: '',
      filePath: '',
      fileDisplayName: '',
      videoUrl: '',
      // UI-only: whether a Video asset is an upload or an external link.
      videoSource: 'upload',
    },
  })
  const queryClient = useQueryClient()

  // Track the in-flight S3 upload so Create Asset stays disabled until it
  // settles. filePath only updates when the upload finishes, so without this a
  // swapped file (upload A done, upload B in flight) could be submitted with
  // A's path (RSCX-363).
  const [isUploading, setIsUploading] = useState(false)

  const selectedType = form.watch('type')
  const filePath = form.watch('filePath')
  const videoUrl = form.watch('videoUrl')
  const videoSource = form.watch('videoSource')
  const typeMeta = ASSET_TYPES.find((t) => t.id === selectedType)

  // Video assets can link out to YouTube/etc. instead of uploading a file.
  const isVideoUrl = selectedType === 'Video' && videoSource === 'url'
  const canSubmit = isVideoUrl ? Boolean(videoUrl?.trim()) : Boolean(filePath)

  const isCustom = Boolean(entityId || organizationId)
  const visibleTypes = ASSET_TYPES.filter((t) => !t.xverseOnly || isCustom)

  // S3 prefix segregates uploads by owning scope.
  const filePrefix = (() => {
    if (organizationId) return `course-assets/org-${organizationId}/`
    if (entityId) return `course-assets/${entityId}/`
    return 'course-assets/global/'
  })()

  const { isPending, mutate: mutateAsset } = useMutation({
    mutationFn: (data) => addCourseAsset({ entityId, organizationId, ...data }),
    onError: (err) => toast(err.message, 'error'),
    onSuccess: (data) => {
      toast(`${data.name} created`, 'success')
      queryClient.invalidateQueries({
        queryKey: ['course-assets', entityId ?? organizationId ?? null],
      })
      onCreated?.(data)
      close()
    },
  })

  const onSubmit = ({ videoSource: _src, ...data }) => {
    // Guard the form's native submit path (Enter key) the same way the
    // Create Asset button is disabled while an upload is in flight.
    if (isUploading) return
    // Send exactly one of file_path / video_url depending on the chosen source.
    if (isVideoUrl) {
      mutateAsset({ ...data, filePath: '', fileDisplayName: '', videoUrl: data.videoUrl.trim() })
    } else {
      mutateAsset({ ...data, videoUrl: '' })
    }
  }

  return (
    <Modal
      actions={[
        { label: 'Cancel', onClick: close, type: 'cancel' },
        {
          label: isUploading ? 'Uploading…' : 'Create Asset',
          onClick: form.handleSubmit(onSubmit),
          type: 'submit',
          disabled: !canSubmit || isUploading,
        },
      ]}
      content={
        <Form {...form}>
          <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              disabled={isPending}
              name="name"
              rules={{ required: 'Name is required' }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              disabled={isPending}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea rows={3} {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              rules={{ required: 'Pick a file type' }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a type" />
                    </SelectTrigger>
                    <SelectContent>
                      {visibleTypes.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {selectedType === 'Video' && (
              <FormField
                control={form.control}
                name="videoSource"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Video source</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="upload">Upload a file</SelectItem>
                        <SelectItem value="url">Link (YouTube, Vimeo, etc.)</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            )}

            {isVideoUrl && (
              <FormField
                control={form.control}
                disabled={isPending}
                name="videoUrl"
                rules={{
                  required: 'Enter a video URL',
                  pattern: { value: /^https?:\/\/.+/i, message: 'Enter a valid URL' },
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Video URL</FormLabel>
                    <FormControl>
                      <Input placeholder="https://www.youtube.com/watch?v=…" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {selectedType && !isVideoUrl && (
              <FormItem>
                <FormLabel>File</FormLabel>
                <FileUploader
                  acceptedFileTypes={typeMeta?.fileTypes}
                  filePrefix={filePrefix}
                  type="aws"
                  maxFiles={1}
                  handleUploadToServer={async (file) => {
                    form.setValue('filePath', file.fileUploadName, { shouldDirty: true })
                    form.setValue('fileDisplayName', file.fileName, { shouldDirty: true })
                  }}
                  // FilePond lifecycle → isUploading: a file entering the pond
                  // starts an instant upload; processed / aborted / errored all
                  // settle it. Keeps Create Asset disabled mid-upload.
                  onaddfilestart={() => setIsUploading(true)}
                  onprocessfile={() => setIsUploading(false)}
                  onprocessfileabort={() => setIsUploading(false)}
                  onerror={() => setIsUploading(false)}
                />
                {filePath && (
                  <p className="text-muted-foreground text-xs">
                    Uploaded: {form.watch('fileDisplayName')}
                  </p>
                )}
              </FormItem>
            )}
          </form>
        </Form>
      }
      loading={isPending}
      title="Create Course Asset"
      setOpen={close}
    />
  )
}

CreateAssetModal.propTypes = {
  entityId: PropTypes.string,
  organizationId: PropTypes.string,
  close: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
}

export default CreateAssetModal
